import { useCallback, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { cn } from "../../../utils/cn";
import { Paperclip } from "lucide-react";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { ExternalHelpConfigSchema } from "../../../schemas/externalHelpConfigSchema";
import { Button } from "../../../components/button";
import {
  useAddHelpAttachment,
  useGetHelpAttachment,
} from "../../../hooks/common/mutation";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../../components/loader";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import { MAX_SIZE_IN_BYTES } from "../../../constants/common";

const ExternalHelpConfig = () => {
  const { t, i18n } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormWithSchema(ExternalHelpConfigSchema);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");

  const {
    mutateAsync: AddHelpAttachment,
    isPending: isAddHelpAttachmentLoading,
  } = useAddHelpAttachment();

  const {
    mutateAsync: GetHelpAttachment,
    isPending: isGetHelpAttachmentLoading,
  } = useGetHelpAttachment();

  const onSubmit = useCallback(
    async (data: { file: any }) => {
      try {
        const body = new FormData();
        body.append("File", data.file);

        await AddHelpAttachment(body);
        toast.success(t(t("fileUploaded")));
      } catch (error) {
        console.error(error);
        toast.error(t("somethingWentWrong"));
      }
    },
    [AddHelpAttachment, t]
  );

  const handleGetAttachment = useCallback(async () => {
    try {
      const response = await GetHelpAttachment();
      if (response.filePath) {
        const pdfUrl = `${process.env.REACT_APP_API_BASE_URL}${response.filePath}`;
        window.open(pdfUrl, "_blank");
      } else {
        toast.error(t("noFileFound"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("somethingWentWrong"));
    }
  }, [GetHelpAttachment, t]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("externalHelpConfig")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className=" bg-white p-4 rounded-xl flex flex-col gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]"
        >
          <h1 className="sm:text-lg flex items-center gap-2">
            {t("uplaodPDFOrViewFile")}{" "}
            {isGetHelpAttachmentLoading ? (
              <div>
                <Loader size={14} />
              </div>
            ) : (
              <span
                onClick={handleGetAttachment}
                className="text-purple-primary underline font-semibold cursor-pointer"
              >
                {t("here")}
              </span>
            )}
            .
          </h1>

          <div className="sm:w-2/3 md:w-1/2">
            <div className="relative flex flex-col gap-1 w-full">
              <Controller
                name="file"
                control={control}
                render={({ field: { onChange } }) => (
                  <div className="relative flex h-9 w-full border-b-2 border-input bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#8D8D87] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 input-fields">
                    <TextInput
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      required
                      ref={fileRef}
                      onChange={async (e) => {
                        const target = e.target as HTMLInputElement;
                        const file = target.files?.[0];

                        if (file) {
                          const allowedTypes = ["application/pdf"];

                          if (!allowedTypes.includes(file.type)) {
                            toast.error(t("invalidFileTypePDF"));
                            target.value = "";
                            return;
                          }

                          if (file.size > MAX_SIZE_IN_BYTES) {
                            toast.error(t("fileSizeExceedsLimit"));
                            target.value = "";
                            return;
                          }
  
                          onChange(file);
                          setFileName(file.name);
                        }
                      }}
                    />

                    <div className="overflow-hidden">
                      <div className="text-nowrap">
                        {fileName || t("noFileSelected")}
                      </div>

                      <div
                        className={cn(
                          "absolute bottom-1 flex justify-center items-center gap-2 border border-black p-2 sm:px-3 sm:py-2 rounded-full bg-white cursor-pointer",
                          i18n.language === "en" ? "right-0" : "left-0"
                        )}
                        onClick={() => fileRef.current?.click()}
                      >
                        <Paperclip size="15px" />
                        {t("uploadFile")}
                      </div>
                    </div>
                  </div>
                )}
              />

              {errors.file && (
                <p className="text-red-600 italic text-[10px] absolute -bottom-[15px]">
                  {errors.file?.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button isLoading={isAddHelpAttachmentLoading}>{t("save")}</Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ExternalHelpConfig;
